<template>
  <div>
    <v-alert
      v-if="errorMsg"
      class="error-msg"
      :text="errorMsg"
      type="error"
      variant="outlined"
      :closable="true"></v-alert>
    <div class="d-flex flex-column align-center mt-8 intake-detail">
      <v-container :fluid="true" class="lh--container-wide review-container">
        <v-row>
          <v-col cols="12">
            <div class="intake-detail__header">
              <h1 class="intake-detail__title">Medical Necessity</h1>
              <span class="intake-detail__stage-chip">
                <span class="intake-detail__stage-dot" />
                Medical Necessity
              </span>
            </div>
            <ReadOnlyBanner v-if="isReadOnlyDevice" />
          </v-col>
          <v-col cols="12">
            <v-row no-gutters>
              <v-col v-if="!showLoader" cols="12" md="7">
                <PatientBar
                  :patient="patient"
                  :patient-guess="order?.patientGuess"
                  :client-company="order?.clientCompany.id"
                  @highlight-bounding-box="onPatientGuessHighlight" />
              </v-col>
              <v-col v-if="!showLoader" cols="12" md="5">
                <ClassificationBar />
              </v-col>
              <v-container v-if="showLoader">
                <v-skeleton-loader type="text@4" />
              </v-container>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader v-if="showLoader || !currentPage || !order" type="image@8" />
            <PagePreview
              v-if="!showLoader && currentPage && order && requirementsStore.clientConfig"
              ref="previewRef"
              :order-form="order.orderForm"
              :insurance-cards="order.insuranceCard ? [order.insuranceCard] : []"
              :stage="intakeStatus"
              :tags="order?.pageTags[currentPage.id]"
              :is-quick-start-submission="!!order?.isQuickStartSubmission"
              @data-form-selected="onDataFormSelected"
              @attribute-edit="onAttributeEdit"
              @snooze="onSnooze"
              @archive="onArchive">
              <template #before>
                <v-col v-if="shouldShowPolicyReview" cols="12">
                  <PolicyReviewCriteriaCard
                    :order="order"
                    :current-page-id="currentPage?.id"
                    :page-manager="pageManager"
                    @highlight-evidence="onHighlightEvidence" />
                </v-col>
                <v-container v-else-if="checklistGroups && Object.keys(checklistGroups).length">
                  <div class="checklist-intro">
                    <div class="checklist-intro__text">
                      Attach pages needed for prior authorization submission and verify payor requirements are met with
                      the checklist.
                    </div>
                    <div class="checklist-intro__medication">
                      <v-icon size="14">mdi-pill</v-icon>
                      Checklist for {{ checklistMedicationName }}
                    </div>
                  </div>
                  <v-row v-for="[groupName, group] in Object.entries(checklistGroups)" :key="groupName" no-gutters>
                    <v-col class="my-2">
                      <v-card color="surface" class="pa-3">
                        <v-row>
                          <v-col class="weight-600 text-navy-dark">
                            <v-checkbox :model-value="checklistChecks[groupName]" :label="groupName" :readonly="true" />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-expansion-panels color="surface">
                            <v-expansion-panel v-for="[subCategory, items] in Object.entries(group)" :key="subCategory">
                              {{ void (hasCurrentPage = items[0]?.pageIds?.indexOf(currentPage?.id) > -1) }}
                              <v-expansion-panel-title
                                collapse-icon=""
                                expand-icon=""
                                class="text-body-1 text-navy-dark">
                                <v-row class="ma-0">
                                  <v-col cols="12">
                                    {{ subCategory }}
                                  </v-col>
                                  <v-col cols="8">
                                    <span class="text-body-2 text-blue-gray weight-600">Found on:</span>
                                    <v-chip
                                      v-for="pageId in items[0].pageIds
                                        .filter((pageId) => pageManager.getPageNumber(pageId) !== -1)
                                        .sort((a, b) => pageManager.getPageNumber(a) - pageManager.getPageNumber(b))"
                                      :key="pageId"
                                      density="compact"
                                      rounded="sm"
                                      closable
                                      size="small"
                                      color="blue-gray"
                                      class="ml-1"
                                      @click:close="() => onRemovePageCategory(items[0], pageId)">
                                      <template #close>
                                        <v-icon icon="mdi-close" size="15"></v-icon>
                                      </template>
                                      Page {{ pageManager.getPageNumber(pageId) + 1 }}
                                    </v-chip>
                                  </v-col>
                                  <v-col align="right">
                                    <v-btn
                                      v-if="!hasCurrentPage"
                                      size="small"
                                      color="primary"
                                      variant="tonal"
                                      rounded
                                      prepend-icon="mdi-plus"
                                      class="mb-2 checklist-page-btn"
                                      @click.stop="onAddPageCategory(groupName, subCategory)">
                                      Add Page
                                    </v-btn>
                                    <v-btn
                                      v-if="hasCurrentPage"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      rounded
                                      prepend-icon="mdi-minus"
                                      class="mb-2 checklist-page-btn"
                                      @click.stop="onRemovePageCategory(items[0], currentPage?.id)">
                                      Remove Page
                                    </v-btn>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-title>
                              <v-expansion-panel-text>
                                <pre v-for="item in items" :key="item.id" class="unstyled">{{ item.description }}</pre>
                              </v-expansion-panel-text>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-card color="surface" class="pa-3">
                        <v-checkbox
                          v-model="attachPolicySummary"
                          label="Select to attach summary of proof of meeting policy requirements" />
                        <v-btn
                          class="w-100"
                          color="primary"
                          variant="flat"
                          rounded
                          prepend-icon="mdi-file-eye-outline"
                          @click="onPreviewPolicySummary">
                          Preview Policy Summary
                        </v-btn>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-progress-linear color="accent" rounded height="6" :model-value="checklistCompletion" />
                      <div class="checklist-progress-label">{{ checklistCompletion.toFixed(0) }}% complete</div>
                    </v-col>
                  </v-row>
                </v-container>
                <v-col v-if="shouldShowReimbursementTable" cols="12">
                  <ReimbursementDataCard :order="order" />
                </v-col>
              </template>
            </PagePreview>
            <BottomBar v-if="!isReadOnlyDevice" :intake-status="intakeStatus" />
            <div v-if="!isReadOnlyDevice" class="intake-actionbar">
              <v-btn
                variant="outlined"
                color="primary"
                rounded
                prepend-icon="mdi-arrow-left"
                :loading="showLoader"
                @click="onBack">
                Go Back
              </v-btn>
              <v-btn
                v-if="!order?.isQuickStartSubmission"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-card-account-details-outline"
                :loading="showLoader"
                @click="onChangeInsurance">
                Change Insurance
              </v-btn>
              <v-btn
                v-if="!order?.isQuickStartSubmission"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-hand-heart-outline"
                :active="referralThankYouVisible"
                :loading="showLoader"
                @click="onReferralThankYou">
                Referral Thank You
              </v-btn>
              <v-btn
                v-if="!order?.isQuickStartSubmission"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-file-question-outline"
                :active="missingInformationVisible"
                :loading="showLoader"
                @click="onRequestMissingInformation">
                Request Missing Information
              </v-btn>
              <v-btn
                v-if="carequalityEnabled"
                :style="order?.isQuickStartSubmission ? { order: 1 } : null"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-lan-connect"
                :loading="showLoader"
                @click="onSearchNetworkRecords">
                Search Carequality network
                <v-badge
                  v-if="networkSearchInFlight || networkRecordsFound"
                  :content="networkRecordsFound || ''"
                  :color="networkSearchInFlight ? 'warning' : 'success'"
                  :class="{ 'network-badge--running': networkSearchInFlight }"
                  inline
                  :title="networkSearchInFlight ? 'Search running' : 'Records found on the network'" />
                <v-badge
                  v-if="networkDocumentCount"
                  :content="networkDocumentCount"
                  color="accent"
                  inline
                  title="Documents attached to this medical necessity review" />
              </v-btn>
              <div class="intake-actionbar__spacer" />
              <v-btn
                v-if="!order?.isQuickStartSubmission"
                color="primary"
                variant="flat"
                rounded
                append-icon="mdi-arrow-right"
                :loading="showLoader"
                :disabled="buttonDisabled"
                @click="onStartPriorAuth">
                No Missing Information, Start Prior Authorization
              </v-btn>
            </div>
            <v-expand-transition>
              <div v-if="missingInformationVisible" class="intake-actionbar-expansion">
                <OutgoingFaxEditor
                  v-if="order"
                  title="Message to Provider"
                  :types="[TEMPLATE_TYPES.INTAKE_MISSING_INFORMATION]"
                  :intake-order="order"
                  :to-fax-number="order.orderForm?.providerFax"
                  @on-complete="onRequestMissingInformation" />
              </div>
            </v-expand-transition>
            <v-expand-transition>
              <div v-if="referralThankYouVisible" class="intake-actionbar-expansion">
                <OutgoingFaxEditor
                  v-if="order"
                  title="Referral Thank You Message"
                  :types="[TEMPLATE_TYPES.INTAKE_REFERAL_THANK_YOU]"
                  :intake-order="order"
                  :to-fax-number="order.orderForm?.providerFax"
                  @on-complete="onReferralThankYou" />
              </div>
            </v-expand-transition>
          </v-col>
        </v-row>
        <v-row v-if="order">
          <v-col cols="12">
            <!-- Voice AI panel (missing-clinicals agent) is deliberately not mounted here yet; see LAM-5532. -->
            <IntakeTagBar
              :tags="order.statusTags"
              :tag-defs="availableTags"
              :tag-due-dates="order.tagDueDates"
              entity-type="order"
              :entity-id="orderId"
              @update:tags="(t) => (order.statusTags = t)"
              @saved="auditLogListRef?.showLatest()"
              @update:tag-due-dates="(d) => (order.tagDueDates = d)" />
            <AssigneeBar
              :assignees="order.assignees || []"
              entity-type="order"
              :entity-id="orderId"
              @update:assignees="(a) => (order.assignees = a)" />
            <GroupBar
              :assigned-groups="order.assignedGroups || []"
              entity-type="order"
              :entity-id="orderId"
              @update:assigned-groups="(g) => (order.assignedGroups = g)" />
            <SubstatusBar entity-type="order" :entity-id="orderId" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <AuditLogList ref="auditLogListRef" :intake-order-id="orderId" :tag-defs="availableTags" />
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-dialog v-model="showPolicySummary" max-width="900">
      <v-card>
        <v-card-title>Preview Policy Proof Page</v-card-title>
        <v-card-text>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <v-sheet color="surface" class="pa-4" v-html="policySummary"></v-sheet>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showArchiveReasonDialog">
      <ArchiveNote :intake-order="order" @on-archived="onArchived" />
    </v-dialog>
    <NetworkRecordsDialog
      v-if="carequalityEnabled && patient"
      ref="networkRecordsRef"
      v-model="networkRecordsVisible"
      :order-id="orderId"
      :patient-id="patient.id"
      @selection-saved="networkDocumentCount = $event"
      @state-changed="onNetworkStateChanged" />
    <MedicalBenefitDialog
      v-model="medicalBenefitDialogVisible"
      :benefit-type="orderBenefitType"
      :loading="showLoader"
      :error-message="errorMsg"
      @confirm-pharmacy="onConfirmPharmacy"
      @mark-pa-not-required="onMarkPaNotRequired"
      @submit-external="onSubmitExternal" />
  </div>
</template>

<script setup>
import AuditLogList from "@/components/AuditLogList.vue";
import { useDate } from "vuetify";
import { onMounted, onUnmounted, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { intakeService } from "@/services/intakeService";
import ArchiveNote from "@/components/ArchiveNote.vue";
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import BottomBar from "@/components/intakeReview/BottomBar.vue";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import ReimbursementDataCard from "@/components/ReimbursementDataCard.vue";
import PolicyReviewCriteriaCard from "@/components/PolicyReviewCriteriaCard.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import SubstatusBar from "@/components/dashboard/SubstatusBar.vue";
import MedicalBenefitDialog from "@/components/intakeReview/MedicalBenefitDialog.vue";
import NetworkRecordsDialog from "@/components/NetworkRecordsDialog.vue";
import { DOCUMENT_CLASSIFICATIONS, INTAKE_STAGE, IntakeDocumentPage, IntakeOrder } from "@/models/intake";
import { useIntakeStore } from "@/stores/intakeStore";
import { TEMPLATE_TYPES } from "@/models/templates";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useIntakeListFiltersStore } from "@/stores/intakeListFiltersStore";
import { handleFormValidationErrors } from "@/utils/validators";
import ReadOnlyBanner from "@/components/dashboard/ReadOnlyBanner.vue";
import { useResponsive } from "@/composables/useResponsive";

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();
const filtersStore = useIntakeListFiltersStore();
const dateAdapter = useDate();
const { isReadOnlyDevice } = useResponsive();
const showLoader = ref(true);
const intakeStatus = ref("Medical Necessity");
const pageManager = intakeStore.pageManager;
const currentPage = computed(() => intakeStore.pageManager.currentPage);
const route = useRoute();
const orderId = Number.parseInt(route.params.id);
const order = computed(() => intakeStore.intakeOrder);
const patient = computed(() => order.value?.patient ?? null);
const router = useRouter();
const errorMsg = ref(undefined);
const missingInformationVisible = ref(false);
const referralThankYouVisible = ref(false);
const attachPolicySummary = ref(false);
const showPolicySummary = ref(false);
const orderUpdating = ref(false);
const buttonDisabled = ref(false);
const showArchiveReasonDialog = ref(false);
const medicalBenefitDialogVisible = ref(false);
const networkRecordsVisible = ref(false);
const networkRecordsRef = ref(null);
const networkDocumentCount = ref(0);
const networkSearchInFlight = ref(false);
const networkRecordsFound = ref(0);
const networkSelectionSaving = ref(false);

function onNetworkStateChanged({ inFlight, recordCount, saving }) {
  networkSearchInFlight.value = inFlight;
  networkRecordsFound.value = recordCount;
  networkSelectionSaving.value = saving;
}

const waitForNetworkSelectionSave = () =>
  new Promise((resolve) => {
    if (!networkSelectionSaving.value) {
      resolve();
      return;
    }
    const stop = watch(networkSelectionSaving, (saving) => {
      if (!saving) {
        stop();
        resolve();
      }
    });
  });

const onSearchNetworkRecords = async () => {
  if (networkRecordsFound.value || networkSearchInFlight.value) {
    networkRecordsVisible.value = true;
    return;
  }
  await networkRecordsRef.value?.startSearch();
};
const restrictToPharmacy = computed(() => requirementsStore.clientConfig?.intake?.restrictPriorAuthToPharmacy ?? false);
const orderBenefitType = computed(() => order.value?.insuranceCard?.benefitType ?? null);
const availableTags = computed(() => requirementsStore.clientConfig?.intake?.availableTags || []);
const checklistMedicationName = computed(() => {
  // We assume the first checklist item is the medication name
  return order.value?.policyChecklist?.[0]?.medicationName.toUpperCase() || "DEFAULT";
});

const checklistGroups = computed(() => order.value?.checklistGroups);
const checklistCompletion = computed(() => {
  // We only add pages to the first checklist item
  const pageCounts = {};
  order.value.policyChecklist.forEach((item) => {
    if (item.category) {
      pageCounts[item.category] = pageCounts[item.category] ?? 0;
      pageCounts[item.category] += item.pageIds.length;
    }
  });
  const totalCount = Object.keys(pageCounts).length;
  if (!totalCount) {
    return 0;
  }
  const completedCount = Object.values(pageCounts).filter((count) => count > 0).length;
  return Math.round((completedCount / totalCount) * 100);
});
const checklistChecks = computed(() => {
  const checks = {};
  Object.entries(order.value?.checklistGroups).map(([groupName, group]) => {
    checks[groupName] = false;
    Object.values(group).forEach((items) => {
      if (items[0].pageIds.length > 0) {
        checks[groupName] = true;
      }
    });
  });
  return checks;
});

/**
 * Generate a summary of the policy proof page
 * This is used to preview the policy proof page before it is generated
 * The summary is grouped by checklist group and sub-category
 */
const policySummary = computed(() => {
  let summary = "";
  if (!order.value) {
    return summary;
  }

  // Get all pages in order they will appear in the final document
  const allPages = pageManager.pages;

  // Generate the summary grouped by checklist group and sub-category
  Object.entries(order.value.checklistGroups || {}).forEach(([groupName, group]) => {
    summary += `<b style="font-size: 1.1em;">${groupName}</b><br />`;
    Object.entries(group || {}).forEach(([subCategory, items]) => {
      summary += `<b>${subCategory}</b> `;
      items.forEach((item) => {
        const pageNumbers = (item.pageIds || [])
          .filter((pageId) => {
            const pageIndex = allPages.findIndex((p) => p.id === pageId);
            return pageIndex !== -1; // Only include pages that exist
          })
          .sort((a, b) => {
            const pageIndexA = allPages.findIndex((p) => p.id === a);
            const pageIndexB = allPages.findIndex((p) => p.id === b);
            return pageIndexA - pageIndexB;
          })
          .map((pageId) => {
            // Find the index of the page in the full list of pages
            const pageIndex = allPages.findIndex((p) => p.id === pageId);
            return `Page ${pageIndex + 1}`;
          })
          .join(", ");
        if (pageNumbers) {
          summary += `(${pageNumbers})`;
        }
        summary += "<br />";
        summary += item.description.replace(/\n/g, "<br />");
      });
      summary += "<br />";
    });
    summary += "<br />";
  });
  return summary;
});

/**
 * Update the policy summary when the policy summary is generated
 */
watch(policySummary, (newSummary) => {
  if (order.value && attachPolicySummary.value) {
    order.value.updateAttribute("policySummary", btoa(encodeURIComponent(newSummary)));
    order.value.debouncedSaveUpdates();
  }
});

/**
 * Update the policy summary when the checkbox is checked or unchecked
 */
watch(attachPolicySummary, (newAttachPolicySummary) => {
  if (order.value) {
    if (newAttachPolicySummary) {
      order.value.updateAttribute("policySummary", btoa(encodeURIComponent(policySummary.value)));
    } else {
      order.value.updateAttribute("policySummary", "");
    }
    order.value.debouncedSaveUpdates();
  }
});

const previewRef = ref(null);
const auditLogListRef = ref(null);

// Policy evidence boxes are passage-level, so they need less enlargement than intake's single-line
// default (1.5). 1 = the raw box plus the renderer's halo. Raise if highlights read too tight.
const POLICY_HIGHLIGHT_SCALE = 1;

// pageId -> page-manager position; +1 because onHighlightBoundingBox expects a 1-based pageNumber.
// sourceForm=null makes resolveHighlightPageNumber pass it through unchanged (already in page-manager space).
const onPatientGuessHighlight = (data) => {
  previewRef.value?.onHighlightBoundingBox(data, order.value?.orderForm);
};

const onHighlightEvidence = ({ pageId, boundingBox, fieldKey }) => {
  const idx = pageManager.getPageNumber(pageId);
  if (idx === -1) return;
  previewRef.value?.onHighlightBoundingBox(
    { pageNumber: idx + 1, boundingBox, fieldKey, scaleFactor: POLICY_HIGHLIGHT_SCALE },
    null
  );
};

onMounted(async () => {
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.CLINICAL_REVIEW;
  await fetchIntakeOrder();
  await requirementsStore.refreshClientConfig(intakeStore.intakeOrder?.clientCompany?.id);
  await fetchPages();
  if (order.value?.policySummary) {
    attachPolicySummary.value = true;
  }
});

onUnmounted(() => {
  requirementsStore.refreshCurrentUserClientConfig();
});

window.addEventListener("beforeunload", () => {
  // Fire and forget the save updates
  order.value?.saveUpdates();
});

const onPreviewPolicySummary = () => {
  showPolicySummary.value = true;
};

const onAddPageCategory = async (groupName, subCategory) => {
  errorMsg.value = "";
  try {
    orderUpdating.value = true;
    const checklist = order.value.policyChecklist.find(
      (c) => c.category === groupName && c.subCategory === subCategory
    );
    if (checklist && checklist.pageIds.indexOf(currentPage.value.id) === -1) {
      checklist.pageIds.push(currentPage.value.id);
      order.value.updateAttribute("policyChecklist", order.value.policyChecklist);
      await order.value.debouncedSaveUpdates();
    }
  } catch (ex) {
    errorMsg.value = "Error adding the page category";
  } finally {
    orderUpdating.value = false;
  }
};

const onRemovePageCategory = async (checklistItem, pageId) => {
  // We're assuming the checklistItem is a reference from `order.policyChecklist`, so we don't need to find the object again
  checklistItem.pageIds = checklistItem.pageIds.filter((id) => id !== pageId);
  order.value.updateAttribute("policyChecklist", order.value.policyChecklist);
  await order.value.debouncedSaveUpdates();
};

const onChangeInsurance = async () => {
  try {
    showLoader.value = true;
    await intakeService.postUpdateOrderStatus(order.value.id, INTAKE_STAGE.QUALIFY_PATIENT);
    router.push({ name: "qualify-order", params: { id: order.value.id } });
  } catch (ex) {
    errorMsg.value = ex.message;
  } finally {
    showLoader.value = false;
  }
};

const onBack = () => {
  router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
};

const onRequestMissingInformation = () => {
  missingInformationVisible.value = !missingInformationVisible.value;
  if (missingInformationVisible.value) {
    referralThankYouVisible.value = false;
  }
};

const onReferralThankYou = () => {
  referralThankYouVisible.value = !referralThankYouVisible.value;
  if (referralThankYouVisible.value) {
    missingInformationVisible.value = false;
  }
};

const onDataFormSelected = (dataForm) => {
  pageManager.goToDataFormPage(dataForm);
};

const onStartPriorAuth = async () => {
  errorMsg.value = "";
  const isValid = await handleFormValidationErrors(previewRef.value.formErrors);
  if (!isValid) {
    errorMsg.value = "There are some errors with the document data";
    return;
  }
  if (restrictToPharmacy.value && orderBenefitType.value !== "PHARMACY") {
    medicalBenefitDialogVisible.value = true;
    return;
  }
  await submitStep("prior_auth");
};

const submitStep = async (disposition = "prior_auth", externalData = null, benefitType = null) => {
  showLoader.value = true;
  errorMsg.value = "";
  buttonDisabled.value = true;
  try {
    await order.value.saveUpdates();
    await waitForNetworkSelectionSave();
    const payload = { disposition };
    if (benefitType) {
      payload.benefitType = benefitType;
    }
    if (disposition === "external_medical" && externalData) {
      payload.externalPortal = externalData.portal;
      payload.externalReference = externalData.reference;
      payload.externalOutcome = externalData.outcome;
      payload.externalNotes = externalData.notes;
    }
    await intakeService.postOrdersSubmitClinicalReview(order.value.id, payload);
    // Redirect to kanban board, depending on submission method (i.e. CoverMyMeds) some orders will
    // require standby time before status will switch from Created to Requires Review
    router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
    return true;
  } catch (ex) {
    errorMsg.value = `Error submitting the review: ${ex.message}`;
    return false;
  } finally {
    showLoader.value = false;
    buttonDisabled.value = false;
  }
};

const onConfirmPharmacy = async () => {
  if (await submitStep("prior_auth", null, "PHARMACY")) {
    medicalBenefitDialogVisible.value = false;
  }
};

const onMarkPaNotRequired = async () => {
  if (await submitStep("pa_not_required", null, "MEDICAL")) {
    medicalBenefitDialogVisible.value = false;
  }
};

const onSubmitExternal = async (externalData) => {
  if (await submitStep("external_medical", externalData, "MEDICAL")) {
    medicalBenefitDialogVisible.value = false;
  }
};

const fetchIntakeOrder = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  try {
    const orderData = await intakeService.getIntakeOrder(orderId);
    const orderObj = new IntakeOrder(orderData, dateAdapter);
    intakeStore.intakeOrder = orderObj;
    intakeStore.insuranceCards = orderObj.insuranceCard ? [orderObj.insuranceCard] : [];
  } catch (ex) {
    errorMsg.value = `Error fetching draft order: ${ex.message ?? ex}`;
  } finally {
    showLoader.value = false;
  }
};

const fetchPages = async () => {
  try {
    const pageResults = await intakeService.getPatientDocumentPages(order.value.patient.id);
    const pages = [];
    const orderFormPage = order.value.orderForm?.pages?.[0];
    if (orderFormPage) {
      pages.push(orderFormPage);
    }
    const insuranceCardPage = order.value.insuranceCard?.pages?.[0];
    if (insuranceCardPage && insuranceCardPage.id !== orderFormPage?.id) {
      pages.push(insuranceCardPage);
    }
    const pagesIds = pages.map((p) => p.id);
    // All pages except the order form and insurance card
    pageResults.results
      .filter((p) => !pagesIds.includes(p.id))
      .forEach((p) => pages.push(new IntakeDocumentPage(p, dateAdapter)));
    if (!pages.length) {
      errorMsg.value = "No document pages found for this order.";
      return;
    }
    intakeStore.setPageManager(pages, currentPage);
  } catch (ex) {
    errorMsg.value = `Error fetching pages: ${ex.message ?? ex}`;
  } finally {
    showLoader.value = false;
  }
};

const onAttributeEdit = (field, value, type) => {
  if (type === DOCUMENT_CLASSIFICATIONS.ORDER_FORM) {
    order.value?.orderForm.updateAttribute(field.key, value);
    order.value?.orderForm.debouncedSaveUpdates();
  } else if (type === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD) {
    order.value?.insuranceCard.updateAttribute(field.key, value);
    order.value?.insuranceCard.debouncedSaveUpdates();
  }
};

const onSnooze = async ({ minutes }) => {
  try {
    errorMsg.value = "";
    showLoader.value = true;
    const response = await intakeService.postSnoozeOrder(orderId, minutes);
    intakeStore.intakeOrder = new IntakeOrder(response, dateAdapter);
    if (minutes > 0) {
      router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
    }
  } catch (e) {
    errorMsg.value = `Error snoozing order: ${e.message}`;
  } finally {
    showLoader.value = false;
  }
};

const onArchive = async () => {
  if (order.value.status === INTAKE_STAGE.ARCHIVED) {
    try {
      await intakeService.deleteArchiveOrder(order.value.id);
      intakeStore.setReadOnly(false);
      return;
    } catch (e) {
      errorMsg.value = `Error unarchiving order: ${e.message}`;
    }
  }
  showArchiveReasonDialog.value = true;
};

const onArchived = () => {
  showArchiveReasonDialog.value = false;
  router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
};

const shouldShowReimbursementTable = computed(() => {
  // LAM-4074: hidden for all clients; restore the reimbursementTableEnabled config check to re-enable.
  return false;
});

const shouldShowPolicyReview = computed(() => {
  return requirementsStore.clientConfig?.intake?.policyReviewEnabled ?? false;
});

const carequalityEnabled = computed(() => {
  return requirementsStore.clientConfig?.intake?.carequalityEnabled ?? false;
});
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";

.checklist-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  margin-bottom: 16px;

  .checklist-intro__text {
    font-size: 14px;
    line-height: 20px;
    color: $lh_color_blue_gray;
    max-width: 420px;
  }

  .checklist-intro__medication {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 12px;
    border-radius: 13px;
    background: rgba($lh_color_blue, 0.08);
    color: $lh_color_blue;
    font-size: 12.5px;
    font-weight: 600;
  }
}

.checklist-page-btn {
  letter-spacing: normal;
  text-transform: none;
  font-weight: 500;
}

.checklist-progress-label {
  font-family: $lh_font_mono;
  font-size: 12px;
  color: $lh_color_blue;
  margin-top: 8px;
}

@keyframes network-badge-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.network-badge--running :deep(.v-badge__badge) {
  animation: network-badge-pulse 1.2s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .network-badge--running :deep(.v-badge__badge) {
    animation: none;
  }
}
</style>
